import React from 'react';
import { useQuery } from 'react-apollo';
import { getClientEnvironmentDetails } from '@sm/utils';
import { ErrorBoundary } from '@sm/webassets';
import contentModuleQuery from './ContentModule.graphql';
import getContentModuleByName from '.';
import { Placement } from '../../types/enums';
import { ContentModuleData } from '../../types/types';
import { EmptyComponent, EmptyComponentProps } from '../helpers/EmptyComponent';
import getLoaderByPlacementName from '../helpers/getLoaderByPlacementName';
import { ContentModuleContextProvider } from '../../hooks/useContentModuleEvent';

type ContentModuleProps = {
  placement: Placement;
  showLoaderOnFetch: boolean;
};

const ContentModule: React.FC<ContentModuleProps> = ({
  placement,
  showLoaderOnFetch,
}) => {
  const { isBrowser } = getClientEnvironmentDetails();
  const pageUrl = isBrowser ? window.location.href : '/';

  const { loading, error, data } = useQuery(contentModuleQuery, {
    variables: {
      placement,
      pageUrl,
    },
  });
  const LoaderCompoent: React.FC = showLoaderOnFetch
    ? getLoaderByPlacementName(placement)
    : EmptyComponent;

  let ModuleComponent:
    | React.FC<ContentModuleData>
    | React.FC<EmptyComponentProps> = EmptyComponent;
  let moduleData: ContentModuleData | null = null;
  if (data?.contentModules?.length) {
    moduleData = data.contentModules.find(
      (module: ContentModuleData) => module.placement === placement
    );
    ModuleComponent = moduleData
      ? getContentModuleByName(moduleData.placement, moduleData.name)
      : EmptyComponent;
  }

  return (
    <Choose>
      <When condition={!!loading}>
        <LoaderCompoent />
      </When>
      <When condition={!!error || !moduleData}>
        <EmptyComponent testId="ContentModule__Empty" />
      </When>
      <Otherwise>
        {moduleData && (
          <ContentModuleContextProvider moduleData={moduleData}>
            <ModuleComponent
              {...moduleData}
              showLoaderOnFetch={showLoaderOnFetch}
            />
          </ContentModuleContextProvider>
        )}
      </Otherwise>
    </Choose>
  );
};

ContentModule.defaultProps = {
  showLoaderOnFetch: false,
};

export const BoundContentModule: React.FC<ContentModuleProps> = props => (
  <ErrorBoundary>
    <ContentModule {...props} />
  </ErrorBoundary>
);
